/**
 * Health-score badge организации (v0.9.63 #9.64).
 * Показывает 0-100 + цветной индикатор (red/yellow/green).
 */
import { useEffect, useState } from 'react';
import { Activity, Loader2 } from 'lucide-react';
import { api } from '../api/client';

interface Health {
  score: number | null;
  color: 'red' | 'yellow' | 'green' | 'gray';
  label: string;
  period_days: number;
  total_tickets: number;
  frt_in_sla_pct: number | null;
  ttr_in_sla_pct: number | null;
  bad_ratings: number;
  overdue_active: number;
}

const COLOR_BG: Record<string, string> = {
  green:  'bg-emerald-900/50 text-emerald-300 border-emerald-800',
  yellow: 'bg-amber-900/50 text-amber-300 border-amber-800',
  red:    'bg-rose-900/50 text-rose-300 border-rose-800',
  gray:   'bg-slate-800 text-slate-400 border-slate-700',
};

export function HealthBadge({ orgId }: { orgId: string }) {
  const [data, setData] = useState<Health | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get<Health>(`/api/v1/organizations/${orgId}/health-score`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [orgId]);

  if (loading) return (
    <span className="text-[10px] text-slate-500 inline-flex items-center gap-1">
      <Loader2 size={9} className="animate-spin" /> health…
    </span>
  );
  if (!data) return null;

  const title = data.score === null
    ? `Нет тикетов за ${data.period_days} дней — нечего считать`
    : `Health ${data.score}/100 за ${data.period_days} дней:\n` +
      `• Тикетов: ${data.total_tickets}\n` +
      `• FRT в SLA: ${data.frt_in_sla_pct}%\n` +
      `• TTR в SLA: ${data.ttr_in_sla_pct}%\n` +
      `• Просрочено активных: ${data.overdue_active}\n` +
      `• Плохие оценки: ${data.bad_ratings}`;

  return (
    <span title={title}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border ${COLOR_BG[data.color]}`}>
      <Activity size={10} />
      {data.score !== null ? `${data.score}` : '—'}
      <span className="opacity-70">{data.label}</span>
    </span>
  );
}
